<template>
  <form>
    <div class="row mb-2">
      <div class="col-sm-12">

        <div class="row mt-2" v-if="!form.id">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.trader") }}:</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`traderId`)">
              <TraderMultiselect
                :class="parentProps.class"
                v-model="form.trader"
                :multiple="false"
                :disabled="!!form.id"
              ></TraderMultiselect>
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.plan_type") }}:</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`planType`)">
              <Multiselect
                :class="parentProps.class"
                v-model="form.planType"
                :options="['limit', 'timer']"
                :custom-label="(opt) => helper.trans(`payout_plans.plan_type.${opt}`)"
                :multiple="false"
                select-label="Select"
                placeholder=""
                @update:modelValue="onPlanTypeChange"
              >
              </Multiselect>
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2" v-if="form.planType === 'limit'">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.limit_amount") }}:</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`limitAmount`)">
              <input :class="parentProps.class" v-model="form.limitAmount" type="text" class="form-control " id="limitAmount">
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2" v-if="form.planType === 'limit'">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.limit_currency") }}:</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`limitCurrency`)">
              <Multiselect
                :class="parentProps.class"
                v-model="form.limitCurrency"
                :options="store.getters['currencyMethods/currencies']"
                :custom-label="(opt) => `${opt}`"
                :multiple="false"
                select-label="Select"
                placeholder=""
              >
              </Multiselect>
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2" v-if="form.planType === 'limit'">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.count_mode") }}:</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`countMode`)">
              <Multiselect
                :class="parentProps.class"
                v-model="form.countMode"
                :options="['all_deals', 'only_success']"
                :custom-label="(opt) => helper.trans(`payout_plans.count_mode.${opt}`)"
                :multiple="false"
                select-label="Select"
                placeholder=""
              >
              </Multiselect>
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2" v-if="form.planType === 'timer'">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.timer_interval") }}:</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`expiredAtManual`)">
            <DatePicker :class="parentProps.class" v-model="form.expiredAtManual" mode="dateTime" :model-modifiers="{string: true}"  is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
              <template v-slot="{ inputValue, togglePopover, inputEvents }">
                <input
                  class="form-control"
                  :placeholder="helper.trans('labels.timer_interval')"
                  :value="inputValue"
                  @focus="togglePopover"
                  @change="inputEvents.change"
                />
              </template>
            </DatePicker>
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2" v-if="form.planType === 'timer'">
          <div class="col-sm-4"></div>
          <div class="col-sm-8">
            <button
              v-for="preset in quickPresets"
              :key="preset.id"
              @click.prevent="applyPreset(preset)"
              type="button"
              class="btn btn-sm btn-outline-secondary me-2 mb-2"
            >
              {{ presetLabel(preset) }}
            </button>
          </div>
        </div>

      </div>
    </div>

  </form>
</template>

<script lang="ts">
import {defineComponent, toRefs, reactive, onMounted} from "vue";
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";
import {api} from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import TraderPayoutPlanFormDTO from "@/models/form/TraderPayoutPlanFormDTO";
import TraderPayoutPlanCreateRequestDTO from "@/models/request/TraderPayoutPlanCreateRequestDTO";
import TraderPayoutPlanUpdateRequestDTO from "@/models/request/TraderPayoutPlanUpdateRequestDTO";
import Multiselect from "vue-multiselect";
import TraderMultiselect from "@/components/TraderMultiselect.vue";
import {DatePicker} from "v-calendar";
import moment from 'moment-timezone';
import {dateHelper} from "@/service/DateHelper";

const notification = useNotification()
export default defineComponent({
  components: {
    Multiselect,
    FormInputWrapper,
    TraderMultiselect,
    DatePicker,
  },
  setup() {
    const store = useStore();
    const state = reactive({
      errors: {} as ValidationErrorDTO[]|any,
      form: {
        expiredAtManual: null,
      } as TraderPayoutPlanFormDTO,
    });

    const setErrors = (data: ValidationErrorDTO[]) => {
      state.errors = {};
      for (const error of data) {
        if (!state.errors.hasOwnProperty(error.field)) {
          state.errors[error.field] = [];
        }
        state.errors[error.field].push(error)
      }
    }

    const getErrors = (path: string) => {
      if (!state.errors.hasOwnProperty(path)) {
        return [];
      }

      return state.errors[path]
    }

    const onPlanTypeChange = (value: string | null) => {
      if (value === 'limit') {
        state.form.expiredAtManual = null;
      } else if (value === 'timer') {
        state.form.limitAmount = null;
        state.form.limitCurrency = null;
        state.form.countMode = null;
      }
    }

    const quickPresets = [
      { id: '5m', type: 'minutes', value: 5 },
      { id: '10m', type: 'minutes', value: 10 },
      { id: '30m', type: 'minutes', value: 30 },
      { id: '1h', type: 'hour_increment', value: 1 },
      { id: '1d', type: 'day_increment', value: 1 },
      { id: '1mo', type: 'month_increment', value: 1 },
    ] as const;

    type QuickPreset = typeof quickPresets[number];

    const applyPreset = (preset: QuickPreset) => {
      const timezone = dateHelper.getUserTimeZone();
      const now = moment().tz(timezone);

      if (preset.type === 'minutes') {
        const base = state.form.expiredAtManual
          ? moment.tz(state.form.expiredAtManual, 'YYYY-MM-DD HH:mm:ss', timezone)
          : now;

        const next = base.isBefore(now) ? now.clone().add(preset.value, 'minute') : base.add(preset.value, 'minute');
        state.form.expiredAtManual = next.format('YYYY-MM-DD HH:mm:ss');
        return;
      }

      if (preset.type === 'hour_increment') {
        const base = state.form.expiredAtManual
          ? moment.tz(state.form.expiredAtManual, 'YYYY-MM-DD HH:mm:ss', timezone)
          : now;

        const next = base.isBefore(now) ? now.clone().add(preset.value, 'hour') : base.add(preset.value, 'hour');
        state.form.expiredAtManual = next.format('YYYY-MM-DD HH:mm:ss');
        return;
      }

      if (preset.type === 'day_increment') {
        const base = state.form.expiredAtManual
          ? moment.tz(state.form.expiredAtManual, 'YYYY-MM-DD HH:mm:ss', timezone)
          : now;

        const next = base.isBefore(now) ? now.clone().add(preset.value, 'day') : base.add(preset.value, 'day');
        state.form.expiredAtManual = next.format('YYYY-MM-DD HH:mm:ss');
        return;
      }

      if (preset.type === 'month_increment') {
        const base = state.form.expiredAtManual
          ? moment.tz(state.form.expiredAtManual, 'YYYY-MM-DD HH:mm:ss', timezone)
          : now;

        const next = base.isBefore(now) ? now.clone().add(preset.value, 'month') : base.add(preset.value, 'month');
        state.form.expiredAtManual = next.format('YYYY-MM-DD HH:mm:ss');
        return;
      }
    }

    const presetLabel = (preset: QuickPreset) => {
      const key = (() => {
        switch (preset.type) {
          case 'minutes':
            return 'labels.minutes_period';
          case 'hour_increment':
            return 'labels.hours_period';
          case 'day_increment':
            return 'labels.days_period';
          case 'month_increment':
            return 'labels.months_period';
          default:
            return null;
        }
      })();

      if (!key) {
        return '';
      }

      const translated = helper.trans(key, {
        minute: preset.value,
        hour: preset.value,
        day: preset.value,
        month: preset.value
      });

      return `+ ${translated}`;
    }

    const save = () => {
      setErrors([]);
      const form = getForm();

      let promise;
      const expiredAtManual = form.expiredAtManual;
      if (form.id) {
        const data = {
          planType: form.planType!,
          limitAmount: form.planType === 'limit' ? form.limitAmount : null,
          limitCurrency: form.planType === 'limit' ? form.limitCurrency : null,
          countMode: form.planType === 'limit' ? form.countMode : null,
          expiredAtManual: form.planType === 'timer' ? dateHelper.toUTC(expiredAtManual) : null,
        } as TraderPayoutPlanUpdateRequestDTO;

        promise = api.call.traderPayoutPlans.update(form.id, data)
      } else {
        if (!form.trader) {
          setErrors([{field: 'traderId', code: 'required', message: 'Trader is required'}]);
          return Promise.reject(new Error('Trader is required'));
        }

        const data = {
          traderId: form.trader.id,
          planType: form.planType!,
          limitAmount: form.planType === 'limit' ? form.limitAmount : null,
          limitCurrency: form.planType === 'limit' ? form.limitCurrency : null,
          countMode: form.planType === 'limit' ? form.countMode : null,
          expiredAtManual: form.planType === 'timer' ? dateHelper.toUTC(expiredAtManual) : null,
        } as TraderPayoutPlanCreateRequestDTO;

        promise = api.call.traderPayoutPlans.create(data)
      }

      promise
        .catch((e: any) => {
          if (e.response && e.request && e.request.status === 400 && e.response.data) {
            setErrors(e.response.data)
          } else {
            console.log(e);
          }

        })

      return promise
    }

    const hasErrors = (path: string) => {
      return state.errors[path]?.length > 0
    }

    const getForm = (): TraderPayoutPlanFormDTO => {
      return state.form
    }

    const setForm = (form: TraderPayoutPlanFormDTO) => {
      state.errors = {}
      state.form = {
        ...form,
        expiredAtManual: form.expiredAtManual,
      }
      if (form.planType === 'limit') {
        state.form.expiredAtManual = null;
      } else if (form.planType === 'timer') {
        state.form.limitAmount = null;
        state.form.limitCurrency = null;
        state.form.countMode = null;
      }
    }

    onMounted(async () => {
    })

    return {
      ...toRefs(state),
      getForm,
      setForm,
      store,
      helper,
      numberFormatter,
      setErrors,
      getErrors,
      hasErrors,
      save,
      onPlanTypeChange,
      quickPresets,
      applyPreset,
      presetLabel,
    }
  }
})
</script>
